import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { SITE } from "@/lib/i18n";
import { Menu, X } from "lucide-react";
import { MultidentLogo } from "@/components/Logo";

export const Navbar = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "technology", label: t.nav.technology },
    { id: "cases", label: t.nav.cases },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="shrink-0">
          <MultidentLogo invert={!scrolled} />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-${l.id}`}
              href={`#${l.id}`}
              className={`text-sm transition-colors ${
                scrolled ? "text-[#1A1A1A]/80 hover:text-[#1A1A1A]" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="language-toggle"
            onClick={toggle}
            className={`hidden sm:inline-flex items-center gap-2 text-xs font-semibold tracking-widest rounded-full px-3 py-2 transition-colors ${
              scrolled
                ? "text-[#1A1A1A] border border-[#1A1A1A]/15 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                : "text-white border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            }`}
          >
            <span className={lang === "es" ? "text-[#D4AF37]" : ""}>ES</span>
            <span className="opacity-30">/</span>
            <span className={lang === "en" ? "text-[#D4AF37]" : ""}>EN</span>
          </button>

          <a
            data-testid="navbar-book-btn"
            href="#contact"
            className={`hidden md:inline-flex btn-premium text-sm font-medium px-5 py-3 rounded-full ${
              scrolled ? "bg-[#1A1A1A] text-white" : "bg-white text-[#1A1A1A]"
            }`}
          >
            {t.nav.book}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 -mr-2 ${scrolled ? "text-[#1A1A1A]" : "text-white"}`}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
            className="lg:hidden bg-white border-t border-[#E5E5E5] overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  className="text-[#1A1A1A] text-base"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="language-toggle-mobile"
                onClick={toggle}
                className="self-start text-xs tracking-widest border border-[#1A1A1A]/15 rounded-full px-3 py-2"
              >
                {lang === "es" ? "EN" : "ES"}
              </button>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="btn-premium bg-[#1A1A1A] text-white text-sm font-medium px-5 py-3 rounded-full text-center"
              >
                {t.nav.book}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
